<template>
    <ToastStack :items="toastItems" @close="closeToast" />
    <div>
        <Head :title="__(title)" />

        <!-- Countdown Deadline (SEMUA DEPARTMENT) -->
        <div
            v-if="showCountdown"
            :class="['rounded p-4 flex items-center gap-5 mb-5', deadlineClass]"
        >
            <TicketCountdown
                :countdown="countdown"
                :text="countdownText"
            />
        </div>

        <div
            v-if="shouldShowBanner"
            :class="['rounded p-4 flex items-center mb-5', bannerClass]"
        >
            <TicketBanner :text="bannerText" />
        </div>

        <div class="flex flex-col lg:flex-row gap-4 items-start">

            <!-- ── LEFT: Ticket Info + Request Content ─────────── -->
            <div class="ticket-page-main">
                <form @submit.prevent="update" class="flex flex-col gap-4">

                    <!-- Card 1: Ticket Information (metadata grid) -->
                    <div class="bg-white rounded-lg shadow">
                        <div :class="['ticket-card-section-head', { collapsed: !accordion.ticketInfo }]" @click="accordion.ticketInfo = !accordion.ticketInfo">
                            <span>{{ __('Ticket Information') }}</span>
                            <svg class="tsh-chevron" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                        </div>
                        <div v-show="accordion.ticketInfo" class="px-6 pt-5 pb-2 -mr-6 flex flex-wrap">
                            <select-edit-input
                                v-if="auth.user.role.slug !== 'customer'" v-model="form.user_id" placeholder="Search customer" :on-input="doFilter"
                                :items="customers" :error="form.errors.user_id" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Customer')" :value="ticket.user"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23"
                            />
                            <select-edit-input
                                v-if="!(hidden_fields && hidden_fields.includes('channel'))" v-model="form.channel_id" placeholder="Search channel"
                                :items="channels" :error="form.errors.channel_id" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Channel')" :value="ticket.channel"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23"
                            />
                            <select-edit-input
                                v-model="form.priority_id" placeholder="Search priority"
                                :items="priorities" :error="form.errors.priority_id" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Priority')" :value="ticket.priority"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23"
                            />
                            <select-edit-input
                                v-if="!(hidden_fields && hidden_fields.includes('department'))" v-model="form.department_id" placeholder="Search department"
                                :items="departments" :error="form.errors.department_id" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Department')" :value="ticket.department"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23"
                            />
                            <select-edit-input
                                v-if="auth.user.role.slug !== 'customer' && !(hidden_fields && hidden_fields.includes('assigned_to'))"
                                v-model="form.assigned_to" placeholder="Search user"
                                :on-input="doFilterUsersExceptCustomer" :items="filteredUsersExceptCustomers"
                                :error="form.errors.assigned_to" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Assigned to')" :value="ticket.assigned_user??'Not Assigned'"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23"
                                :disabled="!form.department_id"
                            />
                            <select-edit-input
                                v-if="!(hidden_fields && hidden_fields.includes('status'))" v-model="form.status_id" placeholder="Select status"
                                :on-input="doFilterStatusesDepartment" :items="statusesDepartmentforEdit"
                                :error="form.errors.status_id" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Status')" :value="ticket.status?ticket.status.name:'Belum diisi'"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23" @focus="doFilterStatusesDepartment"
                            />
                            <select-edit-input
                                v-if="!(hidden_fields && hidden_fields.includes('ticket_type'))" v-model="form.type_id" placeholder="Search type"
                                :items="types" :error="form.errors.type_id" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Ticket type')" :value="ticket.type"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23"
                            />
                            <select-edit-input
                                v-if="!(hidden_fields && hidden_fields.includes('category'))"
                                v-model="form.category_id" placeholder="Search category"
                                :on-input="doFilterCategoriesExpectCustomer" :items="categoriesExceptCustomer"
                                :error="form.errors.category_id" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Category')" :value="ticket.category"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23"
                                @focus="onCategoryFocus"
                            />
                            <select-edit-input
                                v-if="form.department_id === 3"
                                v-model="form.performance_category" placeholder="Select performance"
                                :items="[{ id: 'performing', name: 'Performing Ticket' },{ id: 'non_performing', name: 'Non-Performing Ticket' }]"
                                :error="form.errors.performance_category" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Performance Ticket')" :value="performanceCategoryLabel"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23"
                            />
                            <select-edit-input
                                v-if="form.department_id === 3 || ['admin','super_admin'].includes(auth.user.role.slug)" v-model="form.levelquality_id" placeholder="Search leveling"
                                :items="level_quality" :error="form.errors.levelquality_id" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Level Quality')" :value="ticket.level_quality"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23"
                            />
                            <select-edit-input
                                v-if="form.department_id === 3 || ['admin','super_admin'].includes(auth.user.role.slug)" v-model="form.levelquantity_id" placeholder="Search leveling"
                                :items="level_quantity" :error="form.errors.levelquantity_id" class="pr-6 pb-5 w-full lg:w-1/3"
                                :label="__('Level Quantity')" :value="ticket.level_quantity"
                                :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23"
                            />
                            <div class="pr-6 pb-5 w-full lg:w-1/3 flex flex-col">
                                <div class="form-label">{{ __('Created') }}</div>
                                <div class="text-sm">{{ moment(ticket.created_at).fromNow() }}</div>
                            </div>
                            <div v-if="form.type_id === 9" class="pr-6 pb-5 w-full lg:w-1/3">
                                <label class="form-label">{{ __('Tanggal Deadline') }}</label>
                                <div v-if="['customer', 'general'].includes(auth.user.role.slug) || ticket.closed || ticket.status_id === 23" class="text-sm">{{ ticket.deadline ? moment(ticket.deadline).format("DD MMM YYYY") : '-' }}</div>
                                <input v-if="!['customer', 'general'].includes(auth.user.role.slug) && !ticket.closed && ticket.status_id !== 23"
                                       type="date" class="ticket-filter-input w-full"
                                       :value="toDateInput(form.deadline)"
                                       :disabled="!user_access.ticket.update"
                                       @focus="$event.target.showPicker?.()"
                                       @mousedown="onDateMousedown($event)"
                                       @change="form.deadline = $event.target.value" />
                            </div>
                            <div v-if="form.department_id === 3" class="pr-6 pb-5 w-full lg:w-1/3">
                                <label class="form-label">{{ __('Tanggal Asset Digunakan') }}</label>
                                <div v-if="['customer', 'general'].includes(auth.user.role.slug) || ticket.closed || ticket.status_id === 23" class="text-sm">{{ ticket.date_usedto ? moment(ticket.date_usedto).format("DD MMM YYYY") : '-' }}</div>
                                <input v-if="!['customer', 'general'].includes(auth.user.role.slug) && !ticket.closed && ticket.status_id !== 23"
                                       type="date" class="ticket-filter-input w-full"
                                       :value="toDateInput(form.date_usedto)"
                                       :disabled="!user_access.ticket.update"
                                       @focus="$event.target.showPicker?.()"
                                       @mousedown="onDateMousedown($event)"
                                       @change="form.date_usedto = $event.target.value" />
                            </div>
                            <div v-if="form.type_id === 9" class="pr-6 pb-5 w-full lg:w-1/3">
                                <text-edit-input v-if="form.department_id === 3 || ['admin','super_admin'].includes(auth.user.role.slug)" v-model="form.total_output_bram" :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23" :value="totalOutputValue" :error="form.errors.total_output_bram" :label="__('Total Output')" />
                            </div>
                        </div>
                    </div>

                    <!-- Card 2: Request Content (subject + details) -->
                    <div class="bg-white rounded-lg shadow">
                        <div :class="['ticket-card-section-head', { collapsed: !accordion.requestContent }]" @click="accordion.requestContent = !accordion.requestContent">
                            <span>{{ __('Request Content') }}</span>
                            <svg class="tsh-chevron" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                        </div>
                        <div v-show="accordion.requestContent" class="px-6 pt-5 pb-6 flex flex-col gap-5">
                            <div class="flex flex-col gap-2">
                                <cc-input v-model="form.cc_user_ids" :available-users="cc_available_users" />
                            </div>

                            <text-edit-input v-model="form.subject" :editable="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23" :value="ticket.subject" :error="form.errors.subject" class="w-full" :label="__('Subject')" />

                            <div class="flex flex-col gap-2">
                                <label class="form-label">{{ __('Request Details') }}</label>
                                <div v-if="!enableEditor" class="ticket-details-view">
                                    <div class="prose max-w-none text-sm flex-1" v-html="form.details" />
                                    <icon v-if="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23" name="edit" class="w-4 h-4 cursor-pointer mt-0.5 flex-shrink-0" @click="enableEditor = true" />
                                </div>
                                <div v-if="enableEditor" class="flex items-start gap-2">
                                    <tiptap-editor v-model="form.details" class="flex-1" />
                                    <icon name="close" class="w-4 h-4 cursor-pointer mt-2 flex-shrink-0" @click="enableEditor = false" />
                                </div>
                            </div>

                            <div class="flex flex-col gap-2">
                                <input ref="file" type="file" accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf, .zip" class="hidden" multiple="multiple" @change="fileInputChange" />
                                <!-- Upload zone (hanya ketika bisa edit) -->
                                <div v-if="!ticket.closed && ticket.status_id !== 23"
                                     class="upload-zone"
                                     :class="{ 'upload-zone--dragging': isDragging }"
                                     @click="fileBrowse"
                                     @dragover.prevent="isDragging = true"
                                     @dragleave.prevent="isDragging = false"
                                     @drop.prevent="onDrop">
                                    <div class="upload-zone__icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                                        </svg>
                                    </div>
                                    <div class="upload-zone__text">
                                        <span class="upload-zone__title">{{ isDragging ? 'Lepaskan file di sini' : __('Attach files') }}</span>
                                        <span class="upload-zone__hint">XLS, DOC, PPT, PDF, ZIP, gambar — atau drag &amp; drop ke sini</span>
                                    </div>
                                </div>
                                <!-- File yang sudah ada (dari server) -->
                                <div v-if="attachments.length" class="upload-file-list">
                                    <div v-for="(file, fi) in attachments" :key="fi" class="upload-file-item">
                                        <div class="upload-file-item__icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                            </svg>
                                        </div>
                                        <div class="upload-file-item__info">
                                            <span class="upload-file-item__name">{{ file.name }}</span>
                                            <span class="upload-file-item__size">
                                                {{ getFileSize(file.size) }}<template v-if="file.user"> · <a class="text-blue-500" :href="route('users.edit', file.user.id)">{{ file.user.first_name }} {{ file.user.last_name }}</a></template><template v-if="file.created_at"> · {{ file.created_at }}</template>
                                            </span>
                                        </div>
                                        <div class="flex items-center gap-1 flex-shrink-0">
                                            <button type="button" class="btn text-xs py-1" @click="downloadAttachment(file)">{{ __('Download') }}</button>
                                            <button v-if="!ticket.closed && ticket.status_id !== 23" type="button" class="upload-file-item__remove" @click="removeAttachment(file, fi)" title="Hapus">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <!-- File baru yang akan diupload -->
                                <div v-if="form.files.length" class="upload-file-list">
                                    <div v-for="(file, fi) in form.files" :key="fi" class="upload-file-item">
                                        <div class="upload-file-item__icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                            </svg>
                                        </div>
                                        <div class="upload-file-item__info">
                                            <span class="upload-file-item__name">{{ file.name }}</span>
                                            <span class="upload-file-item__size">{{ getFileSize(file.size) }}</span>
                                        </div>
                                        <button type="button" class="upload-file-item__remove" @click="fileRemove(file, fi)" title="Hapus">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="form.department_id === 3" class="flex flex-col gap-2">
                                <label class="form-label">{{ __('Link Final Output') }}</label>
                                <div v-if="!enableFinalOutputLinkEditor" class="flex items-center gap-2">
                                    <div class="text-sm flex-1">
                                        <a v-if="form.final_output_link" :href="form.final_output_link" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline break-all">{{ form.final_output_link }}</a>
                                        <span v-else class="text-gray-400 italic text-sm">Belum diisi</span>
                                    </div>
                                    <icon v-if="user_access.ticket.update && !ticket.closed && ticket.status_id !== 23" name="edit" class="w-4 h-4 cursor-pointer flex-shrink-0" @click="enableFinalOutputLinkEditor = true" />
                                </div>
                                <div v-if="enableFinalOutputLinkEditor" class="flex items-center gap-2">
                                    <input v-model="form.final_output_link" type="url" placeholder="Insert Link Here.." class="form-input text-sm flex-1" />
                                    <icon name="close" class="w-4 h-4 cursor-pointer flex-shrink-0" @click="enableFinalOutputLinkEditor = false" />
                                </div>
                            </div>
                        </div>

                        <!-- Rating -->
                        <div v-if="ticket.status && ticket.closed && ticket.review" class="px-6 pb-5 flex items-start gap-2 text-sm border-t border-gray-100 pt-4">
                            <strong>{{ __('Rating') }}:</strong><span>{{ ticket.review.rating }}</span><p>{{ ticket.review.review }}</p>
                        </div>
                        <div v-if="ticket.status && ticket.closed && !ticket.review && auth.user.role.slug === 'customer'" class="flex flex-col gap-4 border-t border-gray-100">
                            <div class="ticket-card-section-head">{{ __('Rate this support') }}</div>
                            <div class="px-6 pb-6 flex flex-col gap-4">
                                <div class="star__review flex flex-col gap-2">
                                    <div class="font-semibold text-sm">{{ __('How do you rate this support service?') }}</div>
                                    <div class="star-rating">
                                        <input id="5-stars" v-model="form.rating" type="radio" name="rating" value="5" /><label for="5-stars" class="star">&#9733;</label>
                                        <input id="4-stars" v-model="form.rating" type="radio" name="rating" value="4" /><label for="4-stars" class="star">&#9733;</label>
                                        <input id="3-stars" v-model="form.rating" type="radio" name="rating" value="3" /><label for="3-stars" class="star">&#9733;</label>
                                        <input id="2-stars" v-model="form.rating" type="radio" name="rating" value="2" /><label for="2-stars" class="star">&#9733;</label>
                                        <input id="1-star" v-model="form.rating" type="radio" name="rating" value="1" /><label for="1-star" class="star">&#9733;</label>
                                    </div>
                                </div>
                                <textarea-input v-model="form.review" :error="form.errors.review" class="w-full lg:w-1/2" :label="__('Feedback')" />
                                <div><button class="btn-indigo" type="submit">{{ __('Submit') }}</button></div>
                            </div>
                        </div>

                        <!-- Footer -->
                        <div class="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
                            <button v-if="['admin','super_admin'].includes(auth.user.role.slug) && user_access.ticket.delete" class="btn-danger" tabindex="-1" type="button" @click="destroy">{{ __('Delete') }}</button>
                            <loading-button v-if="!ticket.closed && ticket.status_id !== 23" :loading="form.processing" class="btn-indigo" type="submit">{{ __('Save') }}</loading-button>
                        </div>
                    </div>

                </form>

                <!-- Activity Log (outside form) -->
                <div class="bg-white rounded-lg shadow">
                    <div :class="['ticket-card-section-head', { collapsed: !accordion.activityLog }]" @click="accordion.activityLog = !accordion.activityLog">
                        <span>Log Activity</span>
                        <svg class="tsh-chevron" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                    </div>
                    <div v-show="accordion.activityLog" class="px-6 py-5">
                        <div v-if="ticket.logs && ticket.logs.length" class="activity-timeline">
                            <div
                                v-for="(log, index) in ticket.logs"
                                :key="index"
                                class="activity-timeline__item"
                                :class="{ 'activity-timeline__item--last': index === ticket.logs.length - 1 }"
                            >
                                <div class="activity-timeline__line" v-if="index < ticket.logs.length - 1"></div>
                                <div class="activity-timeline__node">
                                    <img v-if="log.photo" :src="log.photo" class="activity-timeline__avatar activity-timeline__avatar--img" :alt="log.user" />
                                    <span v-else class="activity-timeline__avatar">{{ log.user ? log.user.charAt(0).toUpperCase() : '?' }}</span>
                                </div>
                                <div class="activity-timeline__content">
                                    <div class="activity-timeline__meta">
                                        <span class="activity-timeline__user">{{ log.user }}</span>
                                        <span class="activity-timeline__time" :title="moment(log.created_at).format('DD MMM YYYY, HH:mm')">
                                            {{ moment(log.created_at).fromNow() }}
                                        </span>
                                    </div>
                                    <div class="activity-timeline__action" v-html="log.action" />
                                    <div class="activity-timeline__date">{{ moment(log.created_at).format('DD MMM YYYY, HH:mm') }}</div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="flex flex-col items-center py-6 text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-8 h-8 mb-2 text-gray-300">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span class="text-sm italic">Belum ada aktivitas tercatat.</span>
                        </div>
                    </div>
                </div>

            </div>

            <!-- ── RIGHT: Chat ONLY ───────────────────────────── -->
            <div class="w-full lg:w-2/5 flex-shrink-0">
                <div class="bg-amber-100 border border-amber-400 rounded-lg p-3 mb-3">
                    <div class="flex gap-2">
                        <svg class="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
                        <p class="text-xs text-amber-900 leading-relaxed"><strong>{{ __('Info:') }}</strong> {{ __("Untuk mengaktifkan link, tekan Enter setelah mengetik atau menempel link") }}</p>
                    </div>
                </div>
                <div class="bg-white rounded-lg shadow chat-area comment-box flex flex-col">
                    <div class="ticket-card-section-head">{{ __('Ticket discussion') }}</div>
                    <!-- Search bar -->
                    <div class="px-3 py-2 border-b border-gray-100">
                        <div class="relative">
                            <svg class="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M12.9 14.32a8 8 0 111.414-1.414l4.387 4.387-1.414 1.414-4.387-4.387zM8 14A6 6 0 108 2a6 6 0 000 12z" clip-rule="evenodd"/></svg>
                            <input v-model="chatSearch" type="text" :placeholder="__('Cari pesan...')" class="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 bg-gray-50" />
                            <button v-if="chatSearch" @click="chatSearch = ''" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                            </button>
                        </div>
                        <p v-if="chatSearch && filteredComments.length === 0" class="text-xs text-gray-400 mt-1">Tidak ada pesan ditemukan.</p>
                        <p v-if="chatSearch && filteredComments.length > 0" class="text-xs text-gray-400 mt-1">{{ filteredComments.length }} pesan ditemukan.</p>
                    </div>
                    <div class="messages overflow-auto reverse__order px-4 pt-2" style="max-height: 420px;">
                        <div v-for="(comment, index) in filteredComments.slice().reverse()" :key="index" class="message mb-4 flex">
                            <div v-if="comment.user_id !== user.id" class="flex-2">
                                <div class="w-10 h-10 relative">
                                    <img v-if="comment.user && comment.user.photo_path" :src="comment.user.photo_path" class="w-10 h-10 rounded-full object-cover" :alt="comment.user.first_name" />
                                    <span v-else-if="comment.user" class="w-10 h-10 rounded-full mx-auto user_icon text-sm" :style="{ backgroundColor: userColor(comment.user_id) }" alt="chat-user">{{ Array.from(comment.user.first_name)[0] }}</span>
                                </div>
                            </div>
                            <div v-if="comment.user_id !== user.id" class="flex-1 px-2 mt__wrap">
                                <h3 v-if="comment.user" class="font-semibold pb-1 text-xs pt-1" :style="{ color: userColor(comment.user_id) }">{{ comment.user.first_name }} {{ comment.user.last_name }}</h3>
                                <div class="inline-block user-comment-round p-2 px-3 leading-5 ckc text-sm other-comment-bubble" v-html="comment.details" />
                                <div class="pl-2 mt-1">
                                    <small class="text-gray-400 text-xs">{{ moment(comment.updated_at).fromNow(true) }}</small>
                                    <small v-if="comment.edited_at" class="text-gray-400 text-xs ml-1">· Edited</small>
                                </div>
                            </div>
                            <div v-if="comment.user_id === user.id" class="flex-1 mt__wrap px-2 text-right">
                                <template v-if="editingCommentId === comment.id">
                                    <div class="text-left">
                                        <tiptap-editor v-model="editingCommentContent" :mention="true" placeholder="Edit komentar..." />
                                    </div>
                                    <div class="pr-1 mt-1 flex justify-end gap-1">
                                        <button type="button" @click="cancelEditComment" class="text-xs px-2 py-1 rounded bg-gray-200 text-gray-600">Batal</button>
                                        <button type="button" @click="saveEditComment(comment)" class="text-xs px-2 py-1 rounded text-white" style="background-color:#166534;">Simpan</button>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="comment-hover-wrap">
                                        <div class="flex items-center justify-end gap-2">
                                            <button type="button" @click="startEditComment(comment)" class="comment-edit-btn flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 text-xs" title="Edit komentar">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                Edit
                                            </button>
                                            <div class="inline-block rounded p-2 px-3 leading-5 ckc text-sm own-comment-bubble" v-html="comment.details" />
                                        </div>
                                        <div class="pr-1 mt-1 text-right">
                                            <small class="text-gray-400 text-xs">{{ moment(comment.updated_at).fromNow(true) }}</small>
                                            <small v-if="comment.edited_at" class="text-gray-400 text-xs ml-1">· Edited</small>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div class="pt-3 pb-3 px-3 border-t border-gray-100">
                        <div class="write bg-white shadow flex rounded-lg">
                            <div class="flex-1 ticket_comment_edit">
                                <tiptap-editor v-model="comment" :mention="true" placeholder="Balas tiket... ketik @ untuk mention" />
                            </div>
                            <div class="p-2 flex items-center">
                                <button type="button" class="w-9 h-9 rounded-full flex justify-center items-center" style="background-color:#166534;" @click="submitComment">
                                    <icon class="w-4 h-4 fill-gray-100" name="send" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
    import { Link, Head } from '@inertiajs/vue3'
    import Icon from '@/Shared/Icon'
    import Layout from '@/Shared/Layout'
    import TextInput from '@/Shared/TextInput'
    import TextEditInput from '@/Shared/TextEditInput'
    import SelectInput from '@/Shared/SelectInput'
    import TextareaInput from '@/Shared/TextareaInput'
    import LoadingButton from '@/Shared/LoadingButton'
    import SelectInputFilter from '@/Shared/SelectInputFilter'
    import SelectEditInput from '@/Shared/SelectEditInput'
    import moment from 'moment'
    import axios from 'axios'
    import TiptapEditor from '@/Shared/TiptapEditor.vue'
    import CcInput from '@/Shared/CcInput.vue'
    import { throttle } from 'lodash'
    import TicketCountdown from './components/TicketCountdown.vue'
    import TicketBanner from './components/TicketBanner.vue'
    import ToastStack from '@/Shared/ToastStack'

    export default {
        components: {
            LoadingButton,
            SelectInput,
            TextInput,
            TextEditInput,
            TextareaInput,
            Link,
            Head,
            Icon,
            SelectInputFilter,
            SelectEditInput,
            TiptapEditor,
            CcInput,
            TicketCountdown,
            TicketBanner,
            ToastStack,
        },
        layout: Layout,
        props: {
            title: String,
            ticket: Object,
            channels: Array,
            urgencies: Array,
            priorities: Array,
            levels: Array,
            level_quality: Array,
            level_quantity: Array,
            statuses: Array,
            types: Array,
            departments: Array,
            roles: Array,
            categories: Array,
            customers: Array,
            usersExceptCustomers: Array,
            attachments: Array,
            comments: Array,
            auth: Object,
            hidden_fields: Object,
            deadline: Array,
            date_usedto: Array,
            logs: Array,
            cc_available_users: { type: Array, default: () => [] },
        },
        remember: false,
        data() {
            return {
                user: this.$page.props.auth.user,
                type_status: [],
                categoriesExceptCustomer: [],
                statusesDepartmentforEdit: [],
                filteredUsersExceptCustomers: [],
                comment: '',
                chatSearch: '',
                editingCommentId: null,
                editingCommentContent: '',
                editCustomer: false,
                enableEditor: false,
                enableFinalOutputLinkEditor: false,
                accordion: { ticketInfo: true, requestContent: true, activityLog: true },
                user_access: this.$page.props.auth.user.access,
                toastItems: [],
                form: this.$inertia.form({
                    user_id: this.ticket.user_id,
                    channel_id: this.ticket.channel_id,
                    priority_id: this.ticket.priority_id,
                    level_id: this.ticket.level_id,
                    levelquality_id: this.ticket.levelquality_id,
                    levelquantity_id: this.ticket.levelquantity_id,
                    urgency_id: this.ticket.urgency_id,
                    deadline: this.ticket.deadline,
                    date_usedto: this.ticket.date_usedto,
                    status_id: this.ticket.status_id,
                    department_id: this.ticket.department_id,
                    category_id: this.ticket.category_id,
                    assigned_to: this.ticket.assigned_to,
                    type_id: this.ticket.type_id,
                    performance_category: this.ticket.performance_category,
                    final_output_link: this.ticket.final_output_link ?? '',
                    subject: this.ticket.subject,
                    total_output_bram: this.ticket.total_output_bram != null 
                        ? String(this.ticket.total_output_bram) 
                        : '',
                    details: this.ticket.details,
                    cc_user_ids: this.ticket.cc_user_ids ? [...this.ticket.cc_user_ids] : [],
                    files: this.ticket.files,
                    comments: this.ticket.comments,
                    created_at: this.ticket.created_at,
                    removedFiles: [],
                    rating: 0,
                    review: '',
                }),
                countdownInterval: null,
                countdownText: '',
                deadlineClass: '',
                countdown: { days: '00', hours: '00', minutes: '00', seconds: '00' },
                _notifAudio: null,
                _audioUnlocked: false,
                _notifInterval: null,
                _isInitializing: true,
                _statusChangeInProgress: false,
                isDragging: false,
            }
        },
        watch: {
            'form.status_id': {
                handler(newStatusId, oldStatusId) {
                    // 🔥 FIX: Tambahkan guard untuk mencegah infinite loop
                    if (this._isInitializing || this._statusChangeInProgress) {
                        return
                    }
                    
                    if (newStatusId !== oldStatusId && newStatusId) {
                        this._statusChangeInProgress = true
                        this.doFilterStatusesDepartment('')
                        this.$nextTick(() => {
                            this._statusChangeInProgress = false
                        })
                    }
                },
                immediate: false,
            },
            'form.department_id': {
                handler(newDeptId, oldDeptId) {
                    // 🔥 FIX: Tambahkan guard untuk mencegah infinite loop
                    if (this._isInitializing) {
                        return
                    }
                    
                    if (newDeptId !== oldDeptId) {
                        this.getUsersByDepartment()
                        this.form.assigned_to = null
                    }
                },
                immediate: false,
            },
        },
        created() {
            if (this.auth.user.role.slug === 'customer' && this.statuses.length){
                this.type_status = this.statuses.filter(status=> (status.id === this.form.status_id) || status.name.match(/Close.*/))
            } else {
                this.type_status = this.statuses
            }
            this.moment = moment
            this.initializeFilteredUsers()
        },
        mounted() {
            this._isInitializing = true
            this.startOrStopCountdown()
            this._notifAudio = new Audio('/sound/notif.mp3')
            this._notifAudio.volume = 1
            this._notifAudio.preload = 'auto'
            this.doFilterCategoriesExpectCustomer('')
            this.doFilterStatusesDepartment('')
            this.updateCountdown()
            this.countdownInterval = setInterval(() => {
                this.updateCountdown()
            }, 1000)
            const unlockAudio = () => {
                if (!this._notifAudio || this._audioUnlocked) return

                this._notifAudio.play().then(() => {
                    this._notifAudio.pause()
                    this._notifAudio.currentTime = 0
                    this._audioUnlocked = true
                }).catch(() => {})

                document.removeEventListener('click', unlockAudio)
                document.removeEventListener('keydown', unlockAudio)
            }

            document.addEventListener('click', unlockAudio)
            document.addEventListener('keydown', unlockAudio)

            axios.get('/dashboard/notifications').then(res => {
                if (Array.isArray(res.data) && res.data.length > 0) {
                    if (this._audioUnlocked) {
                        this._notifAudio.currentTime = 0
                        this._notifAudio.play().catch(() => {
                            // Silent catch - autoplay masih diblokir
                        })
                    }
                }
            })
            .catch(() => {
                // sengaja di-silent
            })

            if ('Notification' in window && Notification.permission === 'default') {
                Notification.requestPermission()
            }

            this._notifInterval = setInterval(async () => {
                try {
                    const res = await axios.get('/dashboard/notifications/unread')

                    res.data.forEach(n => {
                        this.showSystemNotification({
                            title: n.title,
                            body: n.body,
                        })

                        if (this._audioUnlocked) {
                            this._notifAudio.currentTime = 0
                            this._notifAudio.play().catch(() => {})
                        }
                    })
                } catch (e) {
                    // Silent catch
                }
            }, 15000)
            
            // 🔥 FIX: Setelah semua initialization selesai, matikan flag
            this.$nextTick(() => {
                setTimeout(() => {
                    this._isInitializing = false
                }, 500)
            })
        },
        methods: {
            userColor(userId) {
                const palette = [
                    '#e53935', '#8e24aa', '#1e88e5', '#00897b',
                    '#43a047', '#fb8c00', '#6d4c41', '#00acc1',
                    '#3949ab', '#d81b60', '#f4511e', '#7cb342',
                ]
                return palette[userId % palette.length]
            },
            toDateInput(date) {
                if (!date) return ''
                return this.moment(date).format('YYYY-MM-DD')
            },
            onDateMousedown(e) {
                const input = e.target
                if (document.activeElement !== input) return
                requestAnimationFrame(() => {
                    if (document.activeElement === input) input.blur()
                })
            },
            closeToast(index) {
                this.toastItems.splice(index, 1)
            },
            addToast(message, type = 'success') {
                this.toastItems.push({
                    message: message,
                    type: type,
                    id: Date.now()
                })
                
                setTimeout(() => {
                    if (this.toastItems.length > 0) {
                        this.toastItems.shift()
                    }
                }, 5000)
            },
            getDeadlineClass(diffDays) {
                if (diffDays <= 3) return 'critical'
                if (diffDays <= 8) return 'warning'
                return 'normal'
            },
            updateCountdown() {
                if (!this.ticket?.deadline) {
                    this.countdownText = 'Tidak ada deadline'
                    return
                }

                const now = moment()
                const end = moment(this.ticket.deadline)
                const diffMs = end.diff(now)
                const diff = moment.duration(diffMs)

                if (diffMs <= 0) {
                    const overdue = moment.duration(-diffMs)

                    this.countdown = {
                        days: String(overdue.days()).padStart(2, '0'),
                        hours: String(overdue.hours()).padStart(2, '0'),
                        minutes: String(overdue.minutes()).padStart(2, '0'),
                        seconds: String(overdue.seconds()).padStart(2, '0'),
                    }

                    this.countdownText = 'Ticket ini melewati DEADLINE'
                    this.deadlineClass = 'critical'
                    return
                }

                this.countdown = {
                    days: String(diff.days()).padStart(2, '0'),
                    hours: String(diff.hours()).padStart(2, '0'),
                    minutes: String(diff.minutes()).padStart(2, '0'),
                    seconds: String(diff.seconds()).padStart(2, '00'),
                }

                this.countdownText = 'Sisa waktu Ticket ini'
                this.deadlineClass = this.getDeadlineClass(diff.days())
            },
            startOrStopCountdown() {
                if (!this.ticket?.deadline || this.ticket.status_id === 3) return

                this.updateCountdown()
                if (this.countdownInterval) {
                    clearInterval(this.countdownInterval)
                }
                this.countdownInterval = setInterval(this.updateCountdown, 1000)
            },
            handleKeydown(event) {
                if (event.key === 'Enter') {
                    event.preventDefault()
                    this.submitComment()
                }
            },
            doFilter(e){
                axios.get(this.route('filter.customers', {search: e.target.value})).then((res)=>{
                    this.customers.splice(0, this.customers.length, ...res.data)
                })
            },
            onCategoryFocus() {
                // Load categories saat focus jika belum ada
                if (this.categoriesExceptCustomer.length === 0) {
                    this.doFilterCategoriesExpectCustomer('')
                }
            },
            doFilterCategoriesExpectCustomer: throttle(function(e) {
                const search = e.target ? e.target.value : (typeof e === 'string' ? e : '')

                if (search.length >= 0) {
                    axios.get(this.route('filter.categoriesdept'), {
                        params: { search: search },
                    }).then(response => {
                        if (Array.isArray(response.data)) {
                            this.categoriesExceptCustomer = response.data
                        }
                    }).catch(error => {
                        this.categoriesExceptCustomer = []
                    })
                }
            }, 150),
            initializeFilteredUsers() {
                if (this.form.department_id) {
                    this.getUsersByDepartment()
                } else {
                    this.filteredUsersExceptCustomers = [...this.usersExceptCustomers]
                    this.filteredUsersExceptCustomers = this.filteredUsersExceptCustomers.map(user => ({
                        ...user,
                        name: user.name || (user.first_name + ' ' + user.last_name).trim(),
                    }))
                }
            },
            getUsersByDepartment() {
                if (this.form.department_id) {
                    this.filteredUsersExceptCustomers = this.usersExceptCustomers.filter(user => {
                        return user.department_id == this.form.department_id ||
                            user.dept_id == this.form.department_id ||
                            (user.department && user.department.id == this.form.department_id)
                    })

                    this.filteredUsersExceptCustomers = this.filteredUsersExceptCustomers.map(user => ({
                        ...user,
                        name: user.name || (user.first_name + ' ' + user.last_name).trim(),
                    }))

                    if (this.filteredUsersExceptCustomers.length > 0) {
                        return
                    }

                    axios.get(this.route('filter.users_by_department'), {
                        params: { department_id: this.form.department_id },
                    })
                    .then((res) => {
                        this.filteredUsersExceptCustomers.splice(0, this.filteredUsersExceptCustomers.length, ...res.data)
                    })
                    .catch((error) => {
                        // Silent catch
                    })
                } else {
                    this.filteredUsersExceptCustomers = [...this.usersExceptCustomers]
                    this.filteredUsersExceptCustomers = this.filteredUsersExceptCustomers.map(user => ({
                        ...user,
                        name: user.name || (user.first_name + ' ' + user.last_name).trim(),
                    }))
                }
            },
            doFilterUsersExceptCustomer(e) {
                const searchTerm = e.target ? e.target.value : e

                if (this.form.department_id) {
                    axios.get(this.route('filter.users_by_department'), {
                        params: {
                            department_id: this.form.department_id,
                            search: searchTerm,
                        },
                    }).then((res) => {
                        this.filteredUsersExceptCustomers.splice(0, this.filteredUsersExceptCustomers.length, ...res.data)
                    }).catch((error) => {
                        const departmentUsers = this.usersExceptCustomers.filter(user =>
                            user.department_id == this.form.department_id,
                        )

                        let filtered
                        if (searchTerm) {
                            filtered = departmentUsers.filter(user => {
                                const fullName = (user.first_name + ' ' + user.last_name).toLowerCase()
                                const firstName = (user.first_name || '').toLowerCase()
                                const lastName = (user.last_name || '').toLowerCase()
                                const email = (user.email || '').toLowerCase()
                                const searchLower = searchTerm.toLowerCase()

                                return fullName.includes(searchLower) ||
                                    firstName.includes(searchLower) ||
                                    lastName.includes(searchLower) ||
                                    email.includes(searchLower)
                            })
                        } else {
                            filtered = departmentUsers
                        }

                        filtered = filtered.map(user => ({
                            ...user,
                            name: (user.first_name + ' ' + user.last_name).trim(),
                        }))

                        this.filteredUsersExceptCustomers.splice(0, this.filteredUsersExceptCustomers.length, ...filtered)
                    })
                } else {
                    axios.get(this.route('filter.users_except_customer', {search: searchTerm})).then((res) => {
                        this.filteredUsersExceptCustomers.splice(0, this.filteredUsersExceptCustomers.length, ...res.data)
                    })
                }
            },
            doFilterStatusesDepartment: throttle(function(e) {
                const search = e.target ? e.target.value : (typeof e === 'string' ? e : '')

                if (search.length >= 0) {
                    axios.get(this.route('filter.statusesedit'), {
                        params: {
                            search: search,
                            ticket_id: this.ticket.id,
                            current_status_id: this.form.status_id,
                        },
                    }).then(response => {
                        this.statusesDepartmentforEdit = response.data
                    }).catch(error => {
                        // Silent catch
                    })
                }
            }, 150),
            onDrop(e) {
                this.isDragging = false
                const dropped = Array.from(e.dataTransfer.files)
                dropped.forEach(f => this.form.files.push(f))
            },
            fileInputChange(e) {
                let selectedFiles = e.target.files
                for (let i = 0; i < selectedFiles.length; i++) {
                    this.form.files.push(selectedFiles[i])
                }
            },
            fileRemove(image, index) {
                this.form.files.splice(index, 1)
            },
            fileBrowse() {
                this.$refs.file.click()
            },
            downloadAttachment(file) {
                const link = document.createElement('a')
                link.href = window.location.origin + '/files/' + file.path
                link.download = file.name
                link.click()
            },
            removeAttachment(file, index) {
                this.attachments.splice(index, 1)
                this.form.removedFiles.push(file.id)
            },
            getFileSize(size) {
                const i = Math.floor(Math.log(size) / Math.log(1024))
                return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'KB', 'MB', 'GB', 'TB'][i]
            },
            uploadFiles() {
                this.form.files = this.$refs.files.files
            },
            update() {
                this.form.post(this.route('tickets.update', this.ticket.id), {
                    onSuccess: () => {
                        this.addToast('Ticket berhasil diupdate!', 'success')
                        if (this.form.deadline) {
                            this.ticket.deadline = this.form.deadline
                        }
                        this.updateCountdown()
                    },
                    onError: () => {
                        this.addToast('Gagal mengupdate ticket!', 'error')
                    },
                })

                this.form.files = []
                this.form.comment = ''
            },
            destroy() {
                if (confirm('Are you sure you want to delete this ticket?')) {
                    this.$inertia.delete(this.route('tickets.destroy', this.ticket.id))
                }
            },
            restore() {
                if (confirm('Are you sure you want to restore this ticket?')) {
                    this.$inertia.put(this.route('tickets.restore', this.ticket.id))
                }
            },
            startEditComment(comment) {
                this.editingCommentId = comment.id
                this.editingCommentContent = comment.details
            },
            cancelEditComment() {
                this.editingCommentId = null
                this.editingCommentContent = ''
            },
            saveEditComment(comment) {
                const isEmpty = this.editingCommentContent.replace(/<[^>]*>/g, '').trim() === ''
                if (isEmpty) return

                axios.put(this.route('ticket.comment.update', comment.id), {
                    comment: this.editingCommentContent,
                    _token: this.$page.props.csrf_token,
                }).then((response) => {
                    const idx = this.comments.findIndex(c => c.id === comment.id)
                    if (idx !== -1) {
                        this.comments[idx].details = response.data.details
                        this.comments[idx].edited_at = response.data.edited_at
                    }
                    this.editingCommentId = null
                    this.editingCommentContent = ''
                }).catch(() => {
                    this.addToast('Gagal menyimpan edit komentar!', 'error')
                })
            },
            submitComment(){
                var that = this
                const isEmpty = this.comment.replace(/<[^>]*>/g, '').trim() === ''
                if (isEmpty) return

                const messageData = {
                    comment: this.comment,
                    user_id: this.user.id,
                    _token: this.$page.props.csrf_token,
                    ticket_id: this.ticket.id,
                }
                this.comment = ''
                axios.post(this.route('ticket.comment'), messageData).then((response) => {
                    if(response.data){
                        that.comments.push(response.data)
                        // that.addToast('Comment berhasil ditambahkan!', 'success')

                        const isRequester = this.user.id === this.ticket.user_id
                        const isAssigned  = this.user.id === this.ticket.assigned_to

                        if (this._notifAudio && this._audioUnlocked && (isRequester || isAssigned)) {
                            this._notifAudio.currentTime = 0
                            this._notifAudio.play().catch(() => {})
                        }

                        // ✅ PERBAIKAN: Decode HTML entities dan bersihkan teks
                        const cleanText = this.decodeHtmlEntities(
                            messageData.comment
                                .replace(/<[^>]*>?/gm, '') // Hapus HTML tags
                                .trim() // Hapus whitespace di awal/akhir
                        )

                        this.showSystemNotification({
                            title: `HELPDESK - ${this.userDisplayName}`,
                            body: `${cleanText.substring(0, 100)}${cleanText.length > 100 ? '...' : ''}`,
                        })
                    }
                    this.$emit('comment submitted!')
                }).catch((error) => {
                    that.addToast('Gagal mengirim comment!', 'error')
                    this.$emit('Error submitting comment:', error)
                })
            },
            decodeHtmlEntities(text) {
                const textarea = document.createElement('textarea')
                textarea.innerHTML = text
                return textarea.value
                    .replace(/\s+/g, ' ') // Ganti multiple spaces dengan single space
                    .trim() // Hapus whitespace di awal/akhir
            },
            showSystemNotification({ title, body }) {
                if (!('Notification' in window)) return
                if (Notification.permission !== 'granted') return

                new Notification(title, {
                    body,
                    icon: '/favicon.png',
                })
            },
        },
        computed: {
            filteredComments() {
                if (!this.chatSearch.trim()) return this.comments
                const q = this.chatSearch.trim().toLowerCase()
                return this.comments.filter(c => {
                    const text = (c.details || '').replace(/<[^>]*>/g, '').toLowerCase()
                    const name = ((c.user?.first_name || '') + ' ' + (c.user?.last_name || '')).toLowerCase()
                    return text.includes(q) || name.includes(q)
                })
            },
            shouldShowBanner() {
                return this.ticket.department_id === 3 && this.ticket.status_id !== 3 && this.ticket.status_id !== 23
            },
            showCountdown() {
                if (!this.ticket) return false

                return (
                    !!this.ticket.deadline &&
                    this.ticket.status_id !== 3 &&
                    this.ticket.status_id !== 23 &&
                    !!this.countdownText
                )
            },
            bannerClass() {
                return this.ticket.status_id === 26 ? 'approved' : 'critical'
            },
            bannerText() {
                if (!this.ticket || this.ticket.status_id == null) return ''

                return this.ticket.status_id === 26
                    ? 'Status Approved. Assets sudah bisa digunakan.'
                    : 'Status belum Approved. Mohon tidak menggunakan Assets.'
            },
            userDisplayName() {
                if (!this.user) return 'Seseorang'
                return `${this.user.first_name || ''} ${this.user.last_name || ''}`.trim()
                    || this.user.email
            },
            totalOutputValue() {
                return this.ticket.total_output_bram != null 
                    ? String(this.ticket.total_output_bram) 
                    : ''
            },
            performanceCategoryLabel() {
                const map = {
                    'performing': 'Performing Ticket',
                    'non_performing': 'Non-Performing Ticket',
                }
                return map[this.ticket.performance_category] || this.ticket.performance_category || 'Belum diisi'
            },
        },
        beforeUnmount() {
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval)
            }
            if (this._notifInterval) {
                clearInterval(this._notifInterval)
            }
        },
    }
</script>

<style>
    /* DEFAULT: Semua link di .ckc */
    .ckc a {
        text-decoration: underline !important;
    }

    .comment-hover-wrap .comment-edit-btn {
        opacity: 0;
        transition: opacity 0.15s;
    }
    .comment-hover-wrap:hover .comment-edit-btn {
        opacity: 1;
    }

    /* SPESIFIK: Link di comment orang lain (background abu gelap) */
    .other-comment-bubble a {
        color: #93c5fd !important;
        font-weight: 500 !important;
    }

    .other-comment-bubble a:hover {
        color: #bfdbfe !important;
    }

    /* SPESIFIK: Link di comment sendiri (background biru) */
    .bg-blue.rounded .ckc a {
        color: #fbbf24 !important; /* Kuning untuk background biru */
        font-weight: 500 !important;
    }

    .bg-blue.rounded .ckc a:hover {
        color: #f59e0b !important;
    }

    /* Editor */
    .ck-content a {
        color: #3b82f6 !important;
        text-decoration: underline !important;
    }
</style>